import type { ReactNode } from 'react';

export type TonoBanner = 'info' | 'aviso' | 'alerta' | 'exito';

interface Props {
  tono: TonoBanner;
  titulo: string;
  children?: ReactNode;
  /** Accion principal, por ejemplo el boton de respaldar. */
  accion?: ReactNode;
}

/**
 * Aviso dentro de la aplicacion.
 *
 * Cada tono lleva un icono ademas del color, para no depender unicamente del
 * color al comunicar el estado (Req. 19.7).
 */
const TONOS: Record<TonoBanner, { clases: string; icono: string; etiqueta: string }> = {
  info: {
    clases: 'bg-entrega-suave border-entrega/30 text-texto',
    icono: 'ℹ️',
    etiqueta: 'Informacion',
  },
  aviso: {
    clases: 'bg-aviso-suave border-aviso/40 text-texto',
    icono: '⚠️',
    etiqueta: 'Aviso',
  },
  alerta: {
    clases: 'bg-alerta-suave border-alerta/40 text-texto',
    icono: '🔴',
    etiqueta: 'Alerta',
  },
  exito: {
    clases: 'bg-exito-suave border-exito/40 text-texto',
    icono: '✅',
    etiqueta: 'Listo',
  },
};

export function Banner({ tono, titulo, children, accion }: Props) {
  const { clases, icono, etiqueta } = TONOS[tono];

  return (
    <div
      className={`rounded-panel border p-4 ${clases}`}
      role={tono === 'alerta' ? 'alert' : 'status'}
    >
      <div className="flex gap-3">
        <span aria-hidden="true" className="text-lg leading-tight">
          {icono}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold leading-snug">
            <span className="sr-only">{etiqueta}: </span>
            {titulo}
          </p>
          {children !== undefined && (
            <div className="mt-1 text-sm text-texto-suave">{children}</div>
          )}
          {accion !== undefined && <div className="mt-3">{accion}</div>}
        </div>
      </div>
    </div>
  );
}
